/**
 * The facilitator dashboard.
 *
 * Four things a volunteer needs and previously had to reach through the CLI over SSH:
 * who is here and whether their consent is in order, what happened in each session,
 * which sessions failed to record and whether they can be recovered, and what the
 * concept graph is apparently missing.
 *
 * Reached at /facilitator. No router: the path is read once in main.tsx, and Caddy
 * already serves index.html for unknown paths. A router would be a dependency and a
 * concept for the sake of two screens.
 *
 * **It is access control now.** Every route behind these tabs requires the facilitator
 * role, and the two write operations are split: a facilitator may reset a learner, only a
 * super admin may delete one. A learner who types this URL gets told plainly that it is
 * not their screen — the server refuses them either way, so hiding the tabs would only
 * turn an answer into a blank page.
 *
 * That was not true until 2026-08-13. The dashboard shipped behind one shared token, said
 * so in this docstring, and the accounts work (D36) is what made the sentence above
 * honest — which had to happen before these tabs could grow buttons that create and
 * delete children's records.
 */

import { Fragment, useCallback, useEffect, useState } from "react";

import { api } from "../api";
import { WhoAmI } from "../auth";
import type {
  AccessEvent,
  AccountRow,
  AdminEvent,
  DeviceRow,
  Lab,
  SafetyFlag,
  LabKnob,
  CostSummary,
  FacilitatorLearner,
  FacilitatorSessionDetail,
  FacilitatorSessionRow,
  Me,
  NewLearnerResult,
  Proposal,
  ReapplyResult,
  RemovalPreview,
} from "../api";

type Tab =
  | "learners"
  | "sessions"
  | "proposals"
  | "cost"
  | "log"
  | "devices"
  | "accounts"
  | "laboratory";

export function Dashboard({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const [tab, setTab] = useState<Tab>("learners");
  const [problem, setProblem] = useState<string | null>(null);

  // A learner reaching this URL gets an answer rather than a broken page. Every request
  // behind it would 403 anyway; this is only about not being baffling.
  if (me.role === "learner") {
    return (
      <main className="setup">
        <h1>QRL Facilitator</h1>
        <div className="problem" role="alert">
          <strong>This screen is for facilitators</strong>
          <p>Your own work is on the tutor screen.</p>
        </div>
        <p>
          <a href="/">Back to Home</a>
        </p>
        <p className="muted">
          <WhoAmI me={me} onSignOut={onSignOut} />
        </p>
      </main>
    );
  }

  // Two tiers above facilitator since D39. An `admin` runs the organisation — accounts,
  // devices, learners. A `super_admin` additionally reaches the Laboratory, which can
  // change what a tutor says to a child with no review, so it is the tier handed out
  // least. The server enforces both; this only decides what to offer.
  const isAdmin = me.role === "admin" || me.role === "super_admin";
  const isSuperAdmin = me.role === "super_admin";
  const tabs: Tab[] = isSuperAdmin
    ? ["learners", "sessions", "proposals", "cost", "log", "devices", "accounts", "laboratory"]
    : isAdmin
      ? ["learners", "sessions", "proposals", "cost", "log", "devices", "accounts"]
      : ["learners", "sessions", "proposals", "cost", "log", "devices"];

  return (
    <div className="app dash">
      <header className="top">
        <span className="brand">QRL Facilitator</span>
        <nav className="dash-tabs">
          {tabs.map((name) => (
            <button
              key={name}
              type="button"
              className={name === tab ? "tab on" : "tab"}
              onClick={() => {
                setProblem(null);
                setTab(name);
              }}
            >
              {name}
            </button>
          ))}
        </nav>
        <WhoAmI me={me} onSignOut={onSignOut} />
        {/* Named "Home" because that is what the screen it leads to is called, and what
            the button on every playground page says. Three different names for one
            destination is how somebody decides they are lost. */}
        <a className="dash-exit" href="/">
          Home
        </a>
      </header>

      {problem && <p className="problem">{problem}</p>}

      <div className="dash-body">
        {tab === "learners" && (
          <Learners
            onProblem={setProblem}
            isAdmin={isAdmin}
            consentVersion={me.consent_sheet_version}
          />
        )}
        {tab === "sessions" && <Sessions onProblem={setProblem} />}
        {tab === "proposals" && <Proposals onProblem={setProblem} />}
        {tab === "cost" && <Cost onProblem={setProblem} />}
        {tab === "log" && <Log onProblem={setProblem} />}
        {tab === "devices" && <Devices onProblem={setProblem} />}
        {tab === "accounts" && isAdmin && <Accounts onProblem={setProblem} me={me} />}
        {tab === "laboratory" && isSuperAdmin && <Laboratory onProblem={setProblem} />}
      </div>
    </div>
  );
}

/** One place to turn a thrown error into a sentence, so every tab reports the same way. */
function useLoader<T>(
  load: () => Promise<T>,
  onProblem: (message: string | null) => void,
  deps: unknown[] = [],
): { data: T | null; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    onProblem(null);
    load()
      .then((value) => {
        if (live) setData(value);
      })
      .catch((error: unknown) =>
        onProblem(error instanceof Error ? error.message : String(error)),
      );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps]);

  return { data, reload: () => setTick((t) => t + 1) };
}

// --------------------------------------------------------------------------- //
// Learners
// --------------------------------------------------------------------------- //

function Learners({
  onProblem,
  isAdmin,
  consentVersion,
}: {
  onProblem: (m: string | null) => void;
  isAdmin: boolean;
  consentVersion: string;
}) {
  const [adding, setAdding] = useState(false);
  const { data, reload } = useLoader(() => api.facilitatorLearners(), onProblem);

  return (
    <>
      <div className="filter">
        <button type="button" className={adding ? "on" : ""} onClick={() => setAdding(!adding)}>
          {adding ? "close" : "add a learner"}
        </button>
      </div>

      {adding && (
        <AddLearner
          consentVersion={consentVersion}
          onProblem={onProblem}
          onAdded={() => {
            setAdding(false);
            reload();
          }}
        />
      )}

      {!data ? (
        <p className="muted">Loading…</p>
      ) : data.length === 0 ? (
        <p className="muted">No learners yet.</p>
      ) : (
        <div className="cards">
          {data.map((learner) => (
            <LearnerCard
              key={learner.code}
              learner={learner}
              isAdmin={isAdmin}
              onProblem={onProblem}
              onChanged={reload}
            />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Adding a learner: the sign-in they will use, and the consent attestation.
 *
 * The consent tick is the most consequential field on the form, so it is not pre-ticked
 * and the sheet version is named next to it. `recorded_by` is the signed-in facilitator,
 * which the server takes from the session rather than from anything typed here — the
 * difference between a record and an assertion.
 */
function AddLearner({
  consentVersion,
  onProblem,
  onAdded,
}: {
  consentVersion: string;
  onProblem: (m: string | null) => void;
  onAdded: () => void;
}) {
  const [nickname, setNickname] = useState("");
  const [membership, setMembership] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [group, setGroup] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<NewLearnerResult | null>(null);

  const submit = async () => {
    setBusy(true);
    onProblem(null);
    try {
      setDone(
        await api.createLearner({
          nickname: nickname.trim(),
          membership_number: membership.trim(),
          passphrase,
          consent_on_file: consent,
          group: group.trim() || undefined,
        }),
      );
    } catch (error) {
      onProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="card wide">
        <div className="card-head">
          <span className="card-title">
            Added {done.nickname} <span className="muted">{done.learner_code}</span>
          </span>
        </div>
        {done.consent_recorded ? (
          <p className="consent consent-ok">
            consent recorded against sheet version {done.consent_sheet_version}
          </p>
        ) : (
          <p className="consent consent-blocked">{done.warning}</p>
        )}
        <p className="card-note">
          Tell them their nickname, membership number and three words. Only the words are
          looked up later — a facilitator sets a new one instead.
        </p>
        <button type="button" className="primary" onClick={onAdded}>
          Done
        </button>
      </section>
    );
  }

  return (
    <section className="card wide">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label>
          Nickname <span className="muted">what they sign in with, and what the tutor calls them</span>
          <input value={nickname} onChange={(e) => setNickname(e.target.value)} spellCheck={false} />
        </label>
        <label>
          Membership number <span className="muted">digits as the register writes them — 114, not QRL-0114</span>
          <input
            value={membership}
            onChange={(e) => setMembership(e.target.value)}
            inputMode="numeric"
            spellCheck={false}
          />
        </label>
        <label>
          Their three words <span className="muted">at least 9 letters</span>
          <input value={passphrase} onChange={(e) => setPassphrase(e.target.value)} />
        </label>
        <label>
          Group <span className="muted">optional</span>
          <input value={group} onChange={(e) => setGroup(e.target.value)} spellCheck={false} />
        </label>

        <label className="tick">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>
            I hold a signed copy of the consent sheet, <strong>version {consentVersion}</strong>,
            for this learner.
          </span>
        </label>
        {!consent && (
          <p className="card-note">
            You can still add them — but they cannot start a session until consent is
            recorded.
          </p>
        )}

        <button
          type="submit"
          className="primary"
          disabled={busy || !nickname.trim() || !membership.trim() || !passphrase.trim()}
        >
          {busy ? "Adding…" : "Add learner"}
        </button>
      </form>
    </section>
  );
}

function LearnerCard({
  learner,
  isAdmin,
  onProblem,
  onChanged,
}: {
  learner: FacilitatorLearner;
  isAdmin: boolean;
  onProblem: (m: string | null) => void;
  onChanged: () => void;
}) {
  const state = !learner.consent_ok ? "blocked" : learner.consent_stale ? "stale" : "ok";
  return (
    <section className="card">
      <div className="card-head">
        <span className="card-title">
          {learner.name} <span className="muted">{learner.code}</span>
        </span>
        {learner.group && <span className="chip">{learner.group}</span>}
      </div>

      <div className={`consent consent-${state}`}>
        {state === "blocked" ? "cannot start a session" : state === "stale" ? "consent out of date" : "consent in order"}
      </div>
      <p className="card-note">{learner.consent_note}</p>

      {learner.domains.length === 0 ? (
        <p className="muted">Nothing started yet.</p>
      ) : (
        <ul className="domain-list">
          {learner.domains.map((domain) => (
            <li key={domain.slug}>
              <span className="domain-name">{domain.slug}</span>
              {/* A count of concepts touched, not a score. Mastery itself is never
                  shown as a number anywhere — see D16. */}
              <span className="domain-progress">
                {domain.started} of {domain.total} concepts started
              </span>
              <span className="muted">
                {domain.sessions} session{domain.sessions === 1 ? "" : "s"}
                {domain.last_session ? `, last ${domain.last_session}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}

      <LearnerActions
        learner={learner}
        isAdmin={isAdmin}
        onProblem={onProblem}
        onChanged={onChanged}
      />
    </section>
  );
}

/**
 * Reset, delete and set-new-words, each behind a confirmation that shows what it will do.
 *
 * The two destructive ones ask the server what would go and show the answer before
 * offering the button — the same preview-then-confirm shape as recovering a rejected
 * update. The counts name the transcript turns, because "1 session" reads as far less
 * than it is when the session holds a whole conversation.
 *
 * Delete only appears for a super admin. It is not merely disabled: an action a
 * facilitator can never take is noise on their screen, and the two operations look
 * similar enough that having only the reversible one visible is a kindness.
 */
function LearnerActions({
  learner,
  isAdmin,
  onProblem,
  onChanged,
}: {
  learner: FacilitatorLearner;
  isAdmin: boolean;
  onProblem: (m: string | null) => void;
  onChanged: () => void;
}) {
  const [mode, setMode] = useState<"none" | "reset" | "delete" | "passphrase">("none");
  const [preview, setPreview] = useState<RemovalPreview | null>(null);
  const [passphrase, setPassphrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const close = () => {
    setMode("none");
    setPreview(null);
    setPassphrase("");
    setNote(null);
  };

  const askFirst = async (which: "reset" | "delete") => {
    setBusy(true);
    onProblem(null);
    try {
      setPreview(
        which === "reset"
          ? await api.resetLearner(learner.code, false)
          : await api.deleteLearner(learner.code, false),
      );
      setMode(which);
    } catch (error) {
      onProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      if (mode === "reset") await api.resetLearner(learner.code, true);
      if (mode === "delete") await api.deleteLearner(learner.code, true);
      close();
      onChanged();
    } catch (error) {
      onProblem(error instanceof Error ? error.message : String(error));
      setBusy(false);
    }
  };

  const savePassphrase = async () => {
    setBusy(true);
    try {
      await api.setLearnerPassphrase(learner.code, passphrase);
      setNote(`New words set for ${learner.name}. Agree them now — they cannot be looked up.`);
      setPassphrase("");
    } catch (error) {
      onProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  if (mode === "none") {
    return (
      <div className="card-actions">
        <button type="button" onClick={() => setMode("passphrase")} disabled={busy}>
          set words
        </button>
        <button type="button" onClick={() => void askFirst("reset")} disabled={busy}>
          reset
        </button>
        {isAdmin && (
          <button
            type="button"
            className="danger"
            onClick={() => void askFirst("delete")}
            disabled={busy}
          >
            delete
          </button>
        )}
      </div>
    );
  }

  if (mode === "passphrase") {
    return (
      <div className="card-confirm">
        {note ? (
          <>
            <p>{note}</p>
            <button type="button" onClick={close}>
              close
            </button>
          </>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void savePassphrase();
            }}
          >
            <label>
              New words for {learner.name}
              <input value={passphrase} onChange={(e) => setPassphrase(e.target.value)} />
            </label>
            <p className="card-note">
              This signs them out everywhere. Agree the new words out loud once — nobody can
              look it up afterwards.
            </p>
            <button type="submit" className="primary" disabled={busy || !passphrase.trim()}>
              {busy ? "Setting…" : "Set words"}
            </button>
            <button type="button" onClick={close}>
              cancel
            </button>
          </form>
        )}
      </div>
    );
  }

  const removing = preview?.counts ?? {};
  const nothing = Object.values(removing).every((n) => n === 0);

  return (
    <div className={mode === "delete" ? "card-confirm danger" : "card-confirm"}>
      <p className="confirm-title">
        {mode === "reset"
          ? `Clear ${learner.name}'s work?`
          : `Delete ${learner.name} completely?`}
      </p>
      {nothing ? (
        <p className="card-note">There is nothing recorded for them yet.</p>
      ) : (
        <ul className="removal">
          {Object.entries(removing)
            .filter(([, n]) => n > 0)
            .map(([label, n]) => (
              <li key={label}>
                {n} {label}
              </li>
            ))}
        </ul>
      )}
      <p className="card-note">
        {mode === "reset"
          ? "Their consent record and sign-in are kept. This cannot be undone."
          : "Everything, including their consent record and their sign-in. This is the erasure a family is promised, and it cannot be undone."}
      </p>
      <button
        type="button"
        className={mode === "delete" ? "danger" : "primary"}
        onClick={() => void confirm()}
        disabled={busy}
      >
        {busy ? "Working…" : mode === "reset" ? "Clear their work" : "Delete permanently"}
      </button>
      <button type="button" onClick={close} disabled={busy}>
        cancel
      </button>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Sessions
// --------------------------------------------------------------------------- //

/**
 * A session a facilitator should actually look at.
 *
 * Their real question is not "what happened" but "what needs me". An update that was
 * rejected means a learner's evidence is sitting unrecorded; a flagged warning means the
 * tutor claimed something the validator thought worth a second opinion. Everything else
 * is background, and on a busy night the background is most of the list — abandoned
 * sessions from someone opening the app and closing it crowd out the two that matter.
 */
function needsAttention(row: FacilitatorSessionRow): boolean {
  // A safety concern always needs attention, whatever happened to the state update — and
  // it is checked first, because a session can perfectly well have recorded its graph
  // update and still be one somebody should read.
  if (row.safety_concern) return true;
  // A recovered session is settled: a facilitator already re-applied it by hand, and
  // the recovery row carries warnings of its own. Without this it would sit in the
  // needs-attention list permanently, which is how a queue stops being believed.
  if (row.recovered) return false;
  return row.update_status === "rejected" || row.warnings > 0;
}

/**
 * The mark on a session somebody should read.
 *
 * Says only how serious, never why. Reading the words is a deliberate second click —
 * a queue is for deciding what to open, and a learner's worst sentence should not be
 * skimmable over somebody's shoulder from a list.
 */
function SafetyBadge({ concern }: { concern: string | null }) {
  if (!concern) return null;
  return (
    <span className={concern === "escalate" ? "badge bad" : "badge warn"}>
      {concern === "escalate" ? "read now" : "read"}
    </span>
  );
}

function Sessions({ onProblem }: { onProblem: (m: string | null) => void }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [onlyAttention, setOnlyAttention] = useState(false);
  const { data, reload } = useLoader(() => api.facilitatorSessions(), onProblem);

  if (!data) return <p className="muted">Loading…</p>;
  if (data.length === 0) return <p className="muted">No sessions yet.</p>;

  const flagged = data.filter(needsAttention);
  const shown = onlyAttention ? flagged : data;

  return (
    <div className="split">
      <div className="session-list">
        <div className="filter">
          <button
            type="button"
            className={onlyAttention ? "tab" : "tab on"}
            onClick={() => setOnlyAttention(false)}
          >
            all {data.length}
          </button>
          <button
            type="button"
            className={onlyAttention ? "tab on" : "tab"}
            onClick={() => setOnlyAttention(true)}
          >
            needs attention {flagged.length}
          </button>
        </div>

        <table className="grid">
          <thead>
            <tr>
              <th>date</th>
              <th>learner</th>
              <th>domain</th>
              <th>recorded</th>
              <th>cost</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr
                key={row.id}
                className={row.id === openId ? "on" : ""}
                onClick={() => setOpenId(row.id)}
              >
                <td>{row.date}</td>
                <td>
                  {row.learner_name} <span className="muted">{row.learner_code}</span>
                </td>
                <td>{row.domain_slug}</td>
                <td>
                  <SafetyBadge concern={row.safety_concern} />
                  <UpdateBadge row={row} />
                </td>
                <td className="num">{row.cost_usd ? `$${row.cost_usd.toFixed(3)}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="session-detail">
        {openId ? (
          <SessionDetail sessionId={openId} onProblem={onProblem} onApplied={reload} />
        ) : (
          <p className="muted">Pick a session to read it.</p>
        )}
      </div>
    </div>
  );
}

function UpdateBadge({ row }: { row: FacilitatorSessionRow }) {
  if (row.update_status === "applied") {
    // Recovered says something different from flagged: the update failed at session
    // end and a facilitator re-ran it. Before this was distinguishable, a recovered
    // session looked exactly like one that passed first time.
    if (row.recovered) {
      return (
        <span className="badge ok">
          written
          <span title="failed at session end; re-applied by a facilitator"> · recovered</span>
        </span>
      );
    }
    return (
      <span className="badge ok">
        written
        {row.repaired && <span title="the model's payload needed repairing"> · repaired</span>}
        {row.warnings > 0 && <span> · {row.warnings} flagged</span>}
      </span>
    );
  }
  if (row.update_status === "rejected") {
    return <span className="badge bad">not written · {row.errors} error{row.errors === 1 ? "" : "s"}</span>;
  }
  return <span className="badge none">{row.status === "open" ? "open" : "nothing recorded"}</span>;
}

/**
 * What was noticed about a session, and the quote that caused it.
 *
 * Sits above the summary because if there is one here it is the reason somebody opened
 * this session. Deliberately plain — no colour beyond the badge, no alarm. The facilitator
 * is the safety layer; this is a note telling them where to look, and dressing it up as an
 * emergency would make the ordinary ones harder to read.
 */
function SafetyPanel({
  flags,
  onProblem,
  onReviewed,
}: {
  flags: SafetyFlag[];
  onProblem: (m: string | null) => void;
  onReviewed: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  const markRead = async (id: string) => {
    setBusy(id);
    onProblem(null);
    try {
      await api.reviewSafetyFlag(id);
      onReviewed();
    } catch (error) {
      onProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="recover">
      <strong>Worth reading</strong>
      {flags.map((flag) => (
        <div key={flag.id} className="safety-flag">
          <p>
            <SafetyBadge concern={flag.concern} />
            {flag.category && <span className="muted"> {flag.category}</span>}
            {flag.source === "model_refusal" && (
              <span className="muted"> · the tutor declined to answer</span>
            )}
          </p>
          {flag.note && <p>{flag.note}</p>}
          {flag.quote && (
            <blockquote className="detail-notes">
              <span className="detail-label">They said</span>
              {flag.quote}
            </blockquote>
          )}
          <p className="muted">
            {flag.reviewed_at ? (
              <>Read by {flag.reviewed_by}.</>
            ) : (
              <button type="button" onClick={() => void markRead(flag.id)} disabled={busy === flag.id}>
                {busy === flag.id ? "…" : "mark as read"}
              </button>
            )}
          </p>
        </div>
      ))}
      <p className="muted">
        Marking it read takes it off the needs-attention list. It stays in the record, with
        your name on it — this is not a way to make something go away.
      </p>
    </div>
  );
}

function SessionDetail({
  sessionId,
  onProblem,
  onApplied,
}: {
  sessionId: string;
  onProblem: (m: string | null) => void;
  onApplied: () => void;
}) {
  const { data, reload } = useLoader(
    () => api.facilitatorSession(sessionId),
    onProblem,
    [sessionId],
  );
  const [preview, setPreview] = useState<ReapplyResult | null>(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async (confirm: boolean) => {
      setBusy(true);
      try {
        const result = await api.facilitatorReapply(sessionId, confirm);
        setPreview(result);
        if (result.applied) {
          reload();
          onApplied();
        }
      } catch (error) {
        onProblem(error instanceof Error ? error.message : String(error));
      } finally {
        setBusy(false);
      }
    },
    [sessionId, reload, onApplied, onProblem],
  );

  useEffect(() => setPreview(null), [sessionId]);

  if (!data) return <p className="muted">Loading…</p>;
  const rejected = data.row.update_status === "rejected";

  return (
    <>
      <div className="detail-head">
        <strong>
          {data.row.learner_name} · {data.row.domain_slug} · {data.row.date}
        </strong>
        <span className="muted">
          {data.row.duration_min ? `${data.row.duration_min} min · ` : ""}
          {data.row.status}
        </span>
      </div>

      {data.safety_flags.length > 0 && (
        <SafetyPanel flags={data.safety_flags} onProblem={onProblem} onReviewed={reload} />
      )}

      {data.row.summary && <p className="detail-summary">{data.row.summary}</p>}
      {data.facilitator_notes && (
        <p className="detail-notes">
          <span className="detail-label">For the facilitator</span>
          {data.facilitator_notes}
        </p>
      )}

      {rejected && (
        <div className="recover">
          <strong>This session was not recorded.</strong>
          <p className="muted">
            The model's answer is stored. Re-running it against the current rules often
            recovers it — the content is usually fine and only its shape was wrong.
          </p>
          <div className="recover-actions">
            <button type="button" onClick={() => void run(false)} disabled={busy}>
              {busy ? "checking…" : "See what it would write"}
            </button>
            {preview?.ok && !preview.applied && (
              <button type="button" className="primary" onClick={() => void run(true)} disabled={busy}>
                Write it to the skill map
              </button>
            )}
          </div>

          {preview && (
            <div className="preview">
              {preview.repairs.map((note, i) => (
                <p key={i} className="repair">
                  repaired: {note}
                </p>
              ))}
              {preview.applied && <p className="applied">Written to the skill map.</p>}
              {!preview.ok && (
                <p className="still-bad">
                  Still rejected. This one needs a person, not a re-run — the errors below
                  are about the content itself.
                </p>
              )}
              {preview.findings.map((f, i) => (
                <p key={i} className={f.severity === "error" ? "finding bad" : "finding warn"}>
                  [{f.code}] {f.path}: {f.message}
                </p>
              ))}
              {preview.deltas.length > 0 && <DeltaTable deltas={preview.deltas} />}
            </div>
          )}
        </div>
      )}

      {data.updates.map((update) => (
        <div key={update.attempt} className="update">
          <div className="update-head">
            attempt {update.attempt} · {update.model} ·{" "}
            {update.applied ? (
              <span className="badge ok">written</span>
            ) : (
              <span className="badge bad">not written</span>
            )}
          </div>
          {[...update.errors, ...update.warnings].map((f, i) => (
            <p key={i} className={f.severity === "error" ? "finding bad" : "finding warn"}>
              [{f.code}] {f.path}: {f.message}
            </p>
          ))}
          {update.deltas.length > 0 && <DeltaTable deltas={update.deltas} />}
        </div>
      ))}

      <h4 className="transcript-head">Transcript</h4>
      <div className="dash-transcript">
        {data.transcript.length === 0 && <p className="muted">Nothing was said.</p>}
        {data.transcript.map((turn, i) => (
          <div key={i} className={`bubble ${turn.role === "assistant" ? "tutor" : "learner"}`}>
            <div className="who">{turn.role === "assistant" ? "Tutor" : data.row.learner_name}</div>
            <div className="text">{turn.text}</div>
            {turn.runs.map((run, j) => (
              <pre key={j} className={`run run-${run.outcome}`}>
                <code>{run.code}</code>
                {run.detail && <span className="run-detail">{run.detail}</span>}
              </pre>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

function DeltaTable({ deltas }: { deltas: FacilitatorSessionDetail["updates"][number]["deltas"] }) {
  return (
    <table className="grid deltas">
      <tbody>
        {deltas.map((delta, i) => (
          <tr key={i}>
            <td className="concept">{delta.concept_id}</td>
            {/* The one place a mastery number is shown to an adult. A facilitator is
                calibrating the tutor's judgement against the anchors, which is what the
                numbers are for; the learner's own map never shows them (D16). */}
            <td className="num">
              {delta.mastery_before.toFixed(2)} → {delta.mastery_after.toFixed(2)}
            </td>
            <td>{delta.status_after}</td>
            <td className="evidence">{delta.evidence}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --------------------------------------------------------------------------- //
// Proposals
// --------------------------------------------------------------------------- //

function Proposals({ onProblem }: { onProblem: (m: string | null) => void }) {
  const [status, setStatus] = useState("pending");
  const { data, reload } = useLoader(
    () => api.facilitatorProposals(status),
    onProblem,
    [status],
  );

  const decide = async (proposal: Proposal, decision: "accepted" | "rejected") => {
    try {
      await api.facilitatorDecideProposal(proposal.id, decision);
      reload();
    } catch (error) {
      onProblem(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <>
      <p className="muted intro">
        Concepts the tutor thinks the graph is missing. Accepting records your judgement
        — it does not edit <code>graph.json</code>. Adding a node is an authoring
        decision, made in the pack.
      </p>
      <div className="filter">
        {["pending", "accepted", "rejected", "all"].map((name) => (
          <button
            key={name}
            type="button"
            className={name === status ? "tab on" : "tab"}
            onClick={() => setStatus(name)}
          >
            {name}
          </button>
        ))}
      </div>

      {!data ? (
        <p className="muted">Loading…</p>
      ) : data.length === 0 ? (
        <p className="muted">Nothing {status === "all" ? "here" : status}.</p>
      ) : (
        <div className="cards">
          {data.map((proposal) => (
            <section className="card" key={proposal.id}>
              <div className="card-head">
                <span className="card-title">
                  <code>{proposal.concept_id}</code>
                </span>
                <span className="chip">{proposal.domain_slug}</span>
              </div>
              {proposal.label && <p className="card-note">{proposal.label}</p>}
              <p className="why">{proposal.why}</p>
              <p className="muted">
                {proposal.learner_code} · {proposal.created_at.slice(0, 10)}
              </p>
              {proposal.status === "pending" ? (
                <div className="recover-actions">
                  <button type="button" className="primary" onClick={() => void decide(proposal, "accepted")}>
                    Worth adding
                  </button>
                  <button type="button" onClick={() => void decide(proposal, "rejected")}>
                    Not needed
                  </button>
                </div>
              ) : (
                <span className={proposal.status === "accepted" ? "badge ok" : "badge none"}>
                  {proposal.status}
                </span>
              )}
            </section>
          ))}
        </div>
      )}
    </>
  );
}

// --------------------------------------------------------------------------- //
// Cost
// --------------------------------------------------------------------------- //

function Cost({ onProblem }: { onProblem: (m: string | null) => void }) {
  const { data } = useLoader<CostSummary>(() => api.facilitatorCost(), onProblem);
  if (!data) return <p className="muted">Loading…</p>;

  const input = data.by_model.reduce((n, r) => n + r.fresh_in + r.cache_read, 0);
  const cached = data.by_model.reduce((n, r) => n + r.cache_read, 0);
  const ratio = input > 0 ? cached / input : 0;

  return (
    <>
      <p className="muted intro">
        The last {data.calls_counted} model calls. Watch the cache column: if reads are
        near zero the prompt prefix has stopped matching and the input bill is roughly
        ten times what it should be.
      </p>
      <table className="grid">
        <thead>
          <tr>
            <th>model</th>
            <th className="num">calls</th>
            <th className="num">fresh in</th>
            <th className="num">cache read</th>
            <th className="num">cache write</th>
            <th className="num">out</th>
            <th className="num">est. USD</th>
          </tr>
        </thead>
        <tbody>
          {data.by_model.map((row) => (
            <tr key={row.model}>
              <td>{row.model}</td>
              <td className="num">{row.calls}</td>
              <td className="num">{row.fresh_in.toLocaleString()}</td>
              <td className="num">{row.cache_read.toLocaleString()}</td>
              <td className="num">{row.cache_write.toLocaleString()}</td>
              <td className="num">{row.out.toLocaleString()}</td>
              <td className="num">${row.usd.toFixed(4)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className={ratio < 0.3 ? "cache-warn" : "muted"}>
        Cache reads are {Math.round(ratio * 100)}% of input tokens. Estimated total:{" "}
        <strong>${data.total_usd.toFixed(4)}</strong>
      </p>
    </>
  );
}

// --------------------------------------------------------------------------- //
// Log
// --------------------------------------------------------------------------- //

/**
 * Who did what to whom.
 *
 * Visible to any facilitator rather than admins only, deliberately: a log that only the
 * person with the most power can read is not much of a check on power. It carries no
 * secrets — the passphrase and a session token never reach it. A membership number may,
 * since D39 made it readable, and that is the one thing here worth knowing about.
 */
/**
 * Devices, the enrolment phrase, and who signed in from where.
 *
 * A facilitator tab rather than an admin one, deliberately: the phrase has to be
 * changeable on the evening it leaks, and the person who notices is the one in the room.
 *
 * Two honesty notes carried from D39 and shown to the facilitator rather than buried
 * here. The phrase is friction against a stranger, not a secret — it gets written on
 * whiteboards and told to parents over the phone. And the sign-in history is personal
 * information about a child, so it is pruned to a short window rather than kept.
 */
function Devices({ onProblem }: { onProblem: (m: string | null) => void }) {
  const [info, setInfo] = useState<Awaited<ReturnType<typeof api.enrolmentInfo>> | null>(null);
  const [devices, setDevices] = useState<DeviceRow[] | null>(null);
  const [access, setAccess] = useState<AccessEvent[] | null>(null);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const reload = useCallback(() => {
    void api.enrolmentInfo().then(setInfo).catch(() => setInfo(null));
    void api.devices().then(setDevices).catch(() => setDevices([]));
    void api.accessLog().then(setAccess).catch(() => setAccess([]));
  }, []);

  useEffect(reload, [reload]);

  const save = async () => {
    setBusy(true);
    onProblem(null);
    try {
      await api.setEnrolmentPhrase(phrase);
      setPhrase("");
      setNote("Phrase set. Devices already enrolled are unaffected.");
      reload();
    } catch (error) {
      onProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    onProblem(null);
    try {
      await api.revokeDevice(id);
      reload();
    } catch (error) {
      onProblem(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <>
      <p className="intro">
        A device answers the QRL phrase once and then goes straight to sign-in every time
        after. It keeps a stranger who found the address from reaching the sign-in form —
        it is not anybody&rsquo;s own login, and it is not a secret: it will end up on a
        whiteboard. Change it when it gets out.
      </p>

      {note && <p className="ok">{note}</p>}

      <div className="panel">
        <p>
          {info?.set
            ? `A phrase is set — last changed by ${info.updated_by ?? "someone"}.`
            : "No phrase is set, so any device can reach the sign-in form."}
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <label>
            New phrase <span className="muted">at least 12 characters</span>
            <input
              value={phrase}
              onChange={(event) => setPhrase(event.target.value)}
              spellCheck={false}
              placeholder="the blue shed by the river"
            />
          </label>
          <button type="submit" className="primary" disabled={busy || phrase.trim().length < 12}>
            {busy ? "Setting…" : info?.set ? "Change the phrase" : "Set a phrase"}
          </button>
        </form>
        <p className="muted">
          Changing it does <strong>not</strong> sign out the devices already enrolled — the
          usual reason to change it is that it got out, and turning every laptop in the room
          back into a new one mid-session would punish everybody for that. Revoke below when
          you do want that.
        </p>
      </div>

      <h3>Enrolled devices</h3>
      {!devices ? (
        <p className="muted">Loading…</p>
      ) : devices.length === 0 ? (
        <p className="muted">Nothing enrolled yet.</p>
      ) : (
        <table className="log">
          <thead>
            <tr>
              <th>label</th>
              <th>first seen</th>
              <th>last seen</th>
              <th>address</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {devices.map((row) => (
              <tr key={row.id}>
                <td>{row.label ?? <span className="muted">unlabelled</span>}</td>
                <td className="muted">{row.created_at.slice(0, 16).replace("T", " ")}</td>
                <td className="muted">{row.last_seen_at.slice(0, 16).replace("T", " ")}</td>
                <td className="muted">{row.last_ip ?? "—"}</td>
                <td className="row-actions">
                  <button type="button" onClick={() => void revoke(row.id)}>
                    revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Recent sign-ins</h3>
      <p className="muted">
        Kept for a short window only. An address plus a time plus an account is a record of
        where a child has been, so this is not somewhere to accumulate history.
      </p>
      {!access ? (
        <p className="muted">Loading…</p>
      ) : (
        <table className="log">
          <thead>
            <tr>
              <th>when</th>
              <th>what</th>
              <th>who</th>
              <th>address</th>
            </tr>
          </thead>
          <tbody>
            {access.map((row, index) => (
              <tr key={`${row.created_at}-${index}`}>
                <td className="muted">{row.created_at.slice(0, 16).replace("T", " ")}</td>
                <td>{row.action.replace(/_/g, " ")}</td>
                <td>
                  {row.nickname ?? <span className="muted">—</span>}
                  {row.code && <span className="muted"> {row.code}</span>}
                </td>
                <td className="muted">{row.ip ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}


function Log({ onProblem }: { onProblem: (m: string | null) => void }) {
  const { data } = useLoader(() => api.adminEvents(), onProblem);
  if (!data) return <p className="muted">Loading…</p>;
  if (data.length === 0) return <p className="muted">Nothing has been changed yet.</p>;

  return (
    <table className="log">
      <thead>
        <tr>
          <th>when</th>
          <th>who</th>
          <th>did what</th>
          <th>to</th>
          <th>detail</th>
        </tr>
      </thead>
      <tbody>
        {data.map((event, index) => (
          <tr key={`${event.at}-${index}`}>
            <td className="muted">{event.at.slice(0, 16).replace("T", " ")}</td>
            <td>
              {event.actor} <span className="muted">{event.actor_role.replace("_", " ")}</span>
            </td>
            <td>
              <span className={event.action.includes("deleted") ? "badge bad" : "badge"}>
                {event.action.replace(/_/g, " ")}
              </span>
            </td>
            <td>{event.subject}</td>
            <td className="muted">{describeDetail(event)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The interesting part of an event's detail, in a sentence rather than as JSON. */
function describeDetail(event: AdminEvent): string {
  const detail = event.detail as {
    removed?: Record<string, number>;
    from?: string;
    to?: string;
    consent_sheet_version?: string | null;
    consent_on_file?: boolean;
    domain?: string;
    sessions_ended?: number;
  };
  if (detail.removed) {
    const parts = Object.entries(detail.removed).filter(([, n]) => n > 0);
    const scope = detail.domain && detail.domain !== "all" ? ` in ${detail.domain}` : "";
    return parts.length === 0
      ? `nothing was recorded yet${scope}`
      : `${parts.map(([label, n]) => `${n} ${label}`).join(", ")}${scope}`;
  }
  if (detail.from && detail.to) return `${detail.from} → ${detail.to}`;
  if (detail.consent_on_file === true) return `consent recorded, sheet v${detail.consent_sheet_version}`;
  if (detail.consent_on_file === false) return "no consent recorded yet";
  if (typeof detail.sessions_ended === "number") {
    return `signed out of ${detail.sessions_ended} session${detail.sessions_ended === 1 ? "" : "s"}`;
  }
  return "";
}

// --------------------------------------------------------------------------- //
// Accounts
// --------------------------------------------------------------------------- //

/**
 * Sign-ins and roles. Super admin only.
 *
 * There are no membership numbers on this screen because there are none stored — only
 * hashes. That is not an omission to be fixed later; it is the point of D36, and the
 * response model has nowhere to put one.
 */
/**
 * Correcting a membership number.
 *
 * Rare, and important when it happens: the number is the readable key into QRL's paper
 * register, so a wrong one is not cosmetic — it is the wrong child on the card.
 *
 * **It sets new words at the same time, and the form says so rather than surprising
 * anybody.** The number is hashed into the credential, so changing it invalidates the
 * stored hash; a facilitator fixing a typo does not know the learner's words and should
 * not, so the only honest options were an account nobody can sign in to, or replacing the
 * secret too.
 */
function CorrectMembership({
  row,
  onProblem,
  onDone,
}: {
  row: AccountRow;
  onProblem: (m: string | null) => void;
  onDone: () => void;
}) {
  const [membership, setMembership] = useState(row.membership_number);
  const [passphrase, setPassphrase] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    onProblem(null);
    try {
      await api.correctMembership(row.code, membership, passphrase);
      onDone();
    } catch (error) {
      onProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  const unchanged = membership.trim() === row.membership_number;

  return (
    <tr className="on">
      <td colSpan={7}>
        <form
          className="panel"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <strong>Correct the membership number for {row.nickname}</strong>
          <p className="muted">
            This also sets new words, because the number is part of what secures the
            account — there is no way to keep the old ones. {row.nickname} is signed out
            everywhere, and you tell them both out loud once.
          </p>
          <label>
            Membership number <span className="muted">digits only — 114, not QRL-0114</span>
            <input
              value={membership}
              onChange={(event) => setMembership(event.target.value)}
              inputMode="numeric"
              spellCheck={false}
            />
          </label>
          <label>
            Their new words <span className="muted">three or more, at least 9 letters</span>
            <input
              value={passphrase}
              onChange={(event) => setPassphrase(event.target.value)}
              spellCheck={false}
            />
          </label>
          <div className="recover-actions">
            <button
              type="submit"
              className="primary"
              disabled={busy || unchanged || passphrase.trim().length < 9}
            >
              {busy ? "Saving…" : "Correct it"}
            </button>
            <button type="button" onClick={onDone} disabled={busy}>
              Cancel
            </button>
          </div>
          {unchanged && <p className="muted">The number is unchanged.</p>}
        </form>
      </td>
    </tr>
  );
}

function Accounts({ onProblem, me }: { onProblem: (m: string | null) => void; me: Me }) {
  const { data, reload } = useLoader(() => api.accounts(), onProblem);
  const [adding, setAdding] = useState(false);

  const [correcting, setCorrecting] = useState<string | null>(null);

  const change = async (
    code: string,
    body: { role?: string; active?: boolean; unlock?: boolean },
  ) => {
    onProblem(null);
    try {
      await api.changeAccount(code, body);
      reload();
    } catch (error) {
      onProblem(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <>
      <p className="intro">
        Facilitators and admins. Learners get their sign-in when they are added on the
        learners tab. Nobody — including you — can read anybody's three words back
        out; they can only be replaced.
      </p>

      <div className="filter">
        <button type="button" className={adding ? "on" : ""} onClick={() => setAdding(!adding)}>
          {adding ? "close" : "add an account"}
        </button>
      </div>

      {adding && (
        <AddAccount
          onProblem={onProblem}
          onAdded={() => {
            setAdding(false);
            reload();
          }}
        />
      )}

      {!data ? (
        <p className="muted">Loading…</p>
      ) : (
        <table className="log">
          <thead>
            <tr>
              <th>code</th>
              <th>nickname</th>
              <th>member</th>
              <th>role</th>
              <th>last sign-in</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {/* Keyed on the code, not the nickname: two learners may both be "Comet"
                since D39, so a nickname is no longer a key for anything. */}
            {data.map((row) => (
              <Fragment key={row.code}>
              <tr className={row.active ? "" : "off"}>
                <td className="muted">{row.code}</td>
                <td>
                  {row.nickname}
                  {row.code === me.code && <span className="muted"> — you</span>}
                </td>
                {/* The membership number is readable since D39 — it is how you find this
                    person in the paper register. It is not, and never was, a secret. */}
                <td className="muted">{row.membership_number}</td>
                <td>
                  <select
                    value={row.role}
                    // Changing your own role is refused by the server too; disabling it
                    // here means the refusal never has to be explained.
                    disabled={row.code === me.code}
                    onChange={(event) => void change(row.code, { role: event.target.value })}
                  >
                    {["learner", "facilitator", "admin", "super_admin"].map((role) => (
                      <option key={role} value={role}>
                        {role.replace("_", " ")}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="muted">
                  {row.last_login_at ? row.last_login_at.slice(0, 16).replace("T", " ") : "never"}
                  {row.locked_minutes !== null && (
                    <span className="badge bad"> locked {row.locked_minutes}m</span>
                  )}
                </td>
                <td className="row-actions">
                  <button
                    type="button"
                    onClick={() => setCorrecting(correcting === row.code ? null : row.code)}
                  >
                    fix number
                  </button>
                  {row.locked_minutes !== null && (
                    <button type="button" onClick={() => void change(row.code, { unlock: true })}>
                      unlock
                    </button>
                  )}
                  {row.code !== me.code && (
                    <button
                      type="button"
                      onClick={() => void change(row.code, { active: !row.active })}
                    >
                      {row.active ? "turn off" : "turn on"}
                    </button>
                  )}
                </td>
              </tr>
              {correcting === row.code && (
                <CorrectMembership
                  row={row}
                  onProblem={onProblem}
                  onDone={() => {
                    setCorrecting(null);
                    reload();
                  }}
                />
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function AddAccount({
  onProblem,
  onAdded,
}: {
  onProblem: (m: string | null) => void;
  onAdded: () => void;
}) {
  const [nickname, setNickname] = useState("");
  const [membership, setMembership] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [role, setRole] = useState("facilitator");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    onProblem(null);
    try {
      await api.createAccount({
        nickname: nickname.trim(),
        membership_number: membership.trim(),
        passphrase,
        role,
      });
      onAdded();
    } catch (error) {
      onProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card wide">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label>
          Nickname
          <input value={nickname} onChange={(e) => setNickname(e.target.value)} spellCheck={false} />
        </label>
        <label>
          QRL membership number <span className="muted">digits only — 114, not QRL-0114</span>
          <input value={membership} onChange={(e) => setMembership(e.target.value)} spellCheck={false} />
        </label>
        <label>
          Their three words <span className="muted">at least 9 letters</span>
          <input value={passphrase} onChange={(e) => setPassphrase(e.target.value)} />
        </label>
        <label>
          Role
          {/* `admin` was missing here until it was noticed while testing something else:
              D39 added the tier and this select was never updated, so the one role that
              runs an organisation without reaching the Laboratory could not be handed
              out from the screen that hands out roles. */}
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="facilitator">facilitator</option>
            <option value="admin">admin</option>
            <option value="super_admin">super admin</option>
          </select>
        </label>
        <button
          type="submit"
          className="primary"
          disabled={busy || !nickname.trim() || !membership.trim() || !passphrase.trim()}
        >
          {busy ? "Adding…" : "Add account"}
        </button>
      </form>
    </section>
  );
}


// --------------------------------------------------------------------------- //
// Tutor Laboratory
// --------------------------------------------------------------------------- //

/**
 * The dials, with their defaults beside them.
 *
 * A playground rather than a control panel, so the ranges are wide and the guardrail is
 * recovery: every row shows what it would be if reset and offers to put it back, and
 * there is one button that resets everything.
 *
 * Two things are always on screen because they are what stop this being a page of
 * unlabelled numbers: **what the setting does** — including what moving it is likely to do
 * — and **when the change lands**. Some take effect on the next request; some only on the
 * next session, because the prompt prefix is assembled once and reused verbatim.
 */
function Laboratory({ onProblem }: { onProblem: (m: string | null) => void }) {
  const [lab, setLab] = useState<Lab | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const load = useCallback(async () => {
    try {
      setLab(await api.lab());
    } catch (error) {
      onProblem(error instanceof Error ? error.message : String(error));
    }
  }, [onProblem]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Returns false when the server refused, so the field can go back to what is stored. */
  const change = async (key: string, value: string | number): Promise<boolean> => {
    setBusy(key);
    onProblem(null);
    try {
      setLab(await api.setLab(key, value));
      return true;
    } catch (error) {
      onProblem(error instanceof Error ? error.message : String(error));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const reset = async (key?: string) => {
    setBusy(key ?? "all");
    try {
      setLab(await api.resetLab(key));
      setConfirmReset(false);
    } catch (error) {
      onProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(null);
    }
  };

  if (!lab) return <p className="muted">Loading…</p>;

  const changed = lab.knobs.filter((k) => k.changed);

  return (
    <>
      <p className="intro">
        A playground. Change something, run a session, see what it does — every dial shows
        its default and can be put back. Nothing here can be set to a value that would stop
        the app working, and <code>qrl lab reset</code> on the server undoes everything if
        this screen ever becomes unreachable.
      </p>

      <div className="filter">
        <span className="muted">
          {changed.length === 0
            ? "everything is at its default"
            : `${changed.length} setting${changed.length === 1 ? "" : "s"} changed`}
        </span>
        {changed.length > 0 &&
          (confirmReset ? (
            <>
              <button type="button" className="danger" onClick={() => void reset()} disabled={busy !== null}>
                yes, reset everything
              </button>
              <button type="button" onClick={() => setConfirmReset(false)}>
                cancel
              </button>
            </>
          ) : (
            <button type="button" onClick={() => setConfirmReset(true)}>
              reset everything
            </button>
          ))}
      </div>

      {lab.groups.map((group) => (
        <section key={group} className="lab-group">
          <h3>{group}</h3>
          {lab.knobs
            .filter((knob) => knob.group === group)
            .map((knob) => (
              <LabRow
                key={knob.key}
                knob={knob}
                busy={busy === knob.key}
                onChange={(value) => change(knob.key, value)}
                onReset={() => void reset(knob.key)}
              />
            ))}
        </section>
      ))}
    </>
  );
}

function LabRow({
  knob,
  busy,
  onChange,
  onReset,
}: {
  knob: LabKnob;
  busy: boolean;
  onChange: (value: string | number) => Promise<boolean>;
  onReset: () => void;
}) {
  const [draft, setDraft] = useState(String(knob.value));

  // The server is the source of truth: after a change or a reset it sends the whole
  // catalogue back, and the field follows it rather than keeping a stale local value.
  useEffect(() => setDraft(String(knob.value)), [knob.value]);

  /**
   * Put the field back when the server refuses.
   *
   * Without this a rejected value stays on screen while the stored one is unchanged — so
   * the panel shows 999 and the tutor uses 12, which is exactly the confusion a
   * configuration screen must not create. The effect above does not cover it: the stored
   * value never changed, so it never re-runs.
   */
  const commit = async () => {
    if (draft === String(knob.value)) return;
    const accepted = await onChange(knob.kind === "choice" ? draft : Number(draft));
    if (!accepted) setDraft(String(knob.value));
  };

  return (
    <div className={knob.changed ? "lab-row changed" : "lab-row"}>
      <div className="lab-head">
        <span className="lab-label">{knob.label}</span>
        <span className="lab-key">{knob.key}</span>
        <span className="lab-lands" title="when a change takes effect">
          {knob.lands}
        </span>
      </div>
      <p className="lab-blurb">{knob.blurb}</p>
      <div className="lab-control">
        {knob.kind === "choice" ? (
          <select
            value={draft}
            disabled={busy}
            onChange={(event) => {
              setDraft(event.target.value);
              void onChange(event.target.value).then((ok) => {
                if (!ok) setDraft(String(knob.value));
              });
            }}
          >
            {knob.choices.map((choice) => (
              <option key={choice} value={choice}>
                {choice}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="number"
            value={draft}
            disabled={busy}
            step={knob.kind === "float" ? 0.05 : 1}
            min={knob.minimum ?? undefined}
            max={knob.maximum ?? undefined}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => void commit()}
            onKeyDown={(event) => {
              if (event.key === "Enter") void commit();
            }}
          />
        )}
        <span className="lab-default">
          default {String(knob.default)}
          {knob.kind !== "choice" && knob.minimum !== null && (
            <> · {knob.minimum}–{knob.maximum}</>
          )}
        </span>
        {knob.changed && (
          <button type="button" onClick={onReset} disabled={busy}>
            reset
          </button>
        )}
      </div>
    </div>
  );
}
